import { buildClassName } from 'utils/build-class-name';
import styles from './collections-tabs.module.scss';

/** The three sections of the Collections pane. */
export type CollectionsTabId = 'favourites' | 'saved-searches' | 'history';

export type CollectionsTab = {
  id: CollectionsTabId;
  label: string;
  /** Total items in the section, shown beside the label. Omit to show no count. */
  count?: number;
};

export type CollectionsTabsProps = {
  tabs: readonly CollectionsTab[];
  activeTab: CollectionsTabId;
  onTabChange: (tab: CollectionsTabId) => void;
  /** Accessible name for the tablist. */
  label: string;
};

/** DOM id of a tab button, referenced by its panel's `aria-labelledby`. */
export function collectionsTabId(tab: CollectionsTabId): string {
  return `AreaPageV2-Collections-Tab-${tab}`;
}

/** DOM id of a tab's panel, referenced by its button's `aria-controls`. */
export function collectionsPanelId(tab: CollectionsTabId): string {
  return `AreaPageV2-Collections-Panel-${tab}`;
}

/**
 * Section switcher for the Collections pane: a plain row of tab buttons. The
 * active one carries its own background; there is no track or sliding indicator.
 */
export function CollectionsTabs({ tabs, activeTab, onTabChange, label }: CollectionsTabsProps) {
  const activeIndex = Math.max(0, tabs.findIndex(tab => tab.id === activeTab));

  return (
    <div
      className={styles.root}
      role="tablist"
      aria-label={label}
      data-testid="AreaPageV2-Collections-Tabs"
    >
      {tabs.map(tab => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            type="button"
            id={collectionsTabId(tab.id)}
            role="tab"
            className={buildClassName(styles.tab, isActive && styles.tabActive)}
            data-testid={`AreaPageV2-Collections-Tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={collectionsPanelId(tab.id)}
            // Only the active tab is in the tab order; arrow keys move between the rest.
            tabIndex={isActive ? 0 : -1}
            onClick={() => onTabChange(tab.id)}
            onKeyDown={event => {
              const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
              if (offset === 0) return;
              event.preventDefault();
              const next = tabs[(activeIndex + offset + tabs.length) % tabs.length];
              onTabChange(next.id);
              document.getElementById(collectionsTabId(next.id))?.focus();
            }}
          >
            <span className={styles.label}>
              {tab.label}
              {typeof tab.count === 'number' && <span className={styles.count}>{tab.count}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}
